import React, { useMemo, useRef, useEffect, useState, useCallback, useLayoutEffect } from "react";
import { useVirtualizer, observeElementRect, observeElementOffset } from "@tanstack/react-virtual";
import { Button } from "@repo/blacksmith-ui/button";
import { ChevronUp, ChevronDown } from "lucide-react";
import { LogLineText } from "./LogLineText";
import type { JobLogsResponse } from "@/hooks/useJobDetails";
interface VirtualizedLogViewerProps {
  logs: JobLogsResponse;
  stepOrder?: number;
  highlightRange?: {
    start: number;
    end: number;
  };
  isLoading?: boolean;
  testId?: string; // Used to track when test changes for auto-scroll reset
  isVisible?: boolean; // Whether the viewer is currently visible (for tab switching)
}
const ESTIMATED_LOG_LINE_HEIGHT = 16;
export function VirtualizedLogViewer({
  logs,
  stepOrder,
  highlightRange,
  isLoading,
  testId,
  isVisible = true
}: VirtualizedLogViewerProps) {
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const [showReturnButton, setShowReturnButton] = useState(false);
  const [isAboveTarget, setIsAboveTarget] = useState(false);
  // Force re-render on width change to re-measure line heights after text reflow
  const [measureKey, setMeasureKey] = useState(0);
  // Preserve scroll position across History/Logs toggles (tanstack-virtual may drift
  // slightly after re-measuring).
  const savedTopIndexRef = useRef<number | null>(null);
  const wasVisibleRef = useRef(isVisible);
  const previousScrollRestoreTestIdRef = useRef<string | null>(testId ?? null);
  const forceMeasureVisibleItems = useCallback(() => {
    if (!logsContainerRef.current) return;
    const container = logsContainerRef.current;
    const items = virtualizerRef.current.getVirtualItems();
    items.forEach(item => {
      const el = container.querySelector<HTMLElement>(`[data-index="${item.index}"]`);
      if (el) {
        virtualizerRef.current.measureElement(el);
      }
    });
  }, []);
  useEffect(() => {
    if (isVisible) {
      requestAnimationFrame(() => {
        forceMeasureVisibleItems();
      });
    }
  }, [isVisible, testId, forceMeasureVisibleItems]);

  // Filter logs for this test's step, or return all logs if no stepOrder specified
  const stepLogs = useMemo(() => {
    if (!logs) return [];
    // If no stepOrder is provided, show all logs
    if (stepOrder == null) return logs;
    return logs.filter(log => log.record_order === stepOrder);
  }, [logs, stepOrder]);
  const getScrollElement = useCallback(() => logsContainerRef.current, []);
  const estimateSize = useCallback(() => ESTIMATED_LOG_LINE_HEIGHT, []);

  // Set up virtualizer for log lines - ALWAYS use the scroll element
  // (component stays mounted with opacity/pointer-events, so DOM state persists)
  // Always use real observers. Swapping observer functions on tab visibility can
  // leave the virtualizer stuck "unmeasured" when it mounted while hidden.
  const virtualizer = useVirtualizer({
    count: stepLogs.length,
    getScrollElement,
    estimateSize,
    overscan: 50,
    observeElementRect,
    observeElementOffset
  });

  // Keep a stable ref so effects don't re-run due to virtualizer instance churn.
  // (A few downstream effects call measure/scroll which can trigger state updates.)
  const virtualizerRef = useRef(virtualizer);
  useEffect(() => {
    virtualizerRef.current = virtualizer;
  }, [virtualizer]);
  const virtualItems = virtualizer.getVirtualItems();

  // Force virtualizer to measure when logs change (not on visibility changes)
  // Gate behind isVisible to avoid triggering measurement while hidden
  useEffect(() => {
    if (stepLogs.length > 0 && isVisible) {
      virtualizerRef.current.measure();
    }
    // NOTE: do not re-measure on visibility changes here; we do a dedicated
    // measure+restore in the layout effect below to avoid scroll drift.
    // (Keeping isVisible out of deps is intentional.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepLogs.length]);

  // Snapshot the current top-most visible virtual row when we hide, so we can restore
  // precisely when toggling back.
  useEffect(() => {
    if (!isVisible && wasVisibleRef.current) {
      const container = logsContainerRef.current;
      const scrollTop = container?.scrollTop ?? 0;

      // IMPORTANT: virtualizer items include overscan above the viewport; don't
      // use the first virtual item as the anchor. Instead, find the item that
      // actually intersects the current scrollTop.
      const items = virtualizerRef.current.getVirtualItems();
      const visibleItem = items.find(item => item.start <= scrollTop && item.start + item.size > scrollTop) ?? items.find(item => item.start >= scrollTop);
      if (visibleItem) {
        savedTopIndexRef.current = visibleItem.index;
      } else if (stepLogs.length > 0) {
        const estimatedIndex = Math.max(0, Math.min(stepLogs.length - 1, Math.floor(scrollTop / ESTIMATED_LOG_LINE_HEIGHT)));
        savedTopIndexRef.current = estimatedIndex;
      } else {
        savedTopIndexRef.current = null;
      }
    }
    wasVisibleRef.current = isVisible;
  }, [isVisible, stepLogs.length, testId]);

  // Clear saved scroll position when switching tests while still visible.
  useEffect(() => {
    if (previousScrollRestoreTestIdRef.current !== (testId ?? null)) {
      savedTopIndexRef.current = null;
      previousScrollRestoreTestIdRef.current = testId ?? null;
    }
  }, [testId]);

  // Force virtualizer to recalculate when becoming visible.
  // This covers cases where the component mounted while hidden and needs a
  // post-layout measurement after the tab switch.
  useLayoutEffect(() => {
    if (isVisible && logsContainerRef.current && stepLogs.length > 0) {
      requestAnimationFrame(() => {
        virtualizerRef.current.measure();
        const savedTopIndex = savedTopIndexRef.current;
        if (savedTopIndex != null) {
          requestAnimationFrame(() => {
            virtualizerRef.current.scrollToIndex(savedTopIndex, {
              align: "start",
              behavior: "auto"
            });
          });
        }
        requestAnimationFrame(() => {
          forceMeasureVisibleItems();
        });
      });
    }
  }, [isVisible, stepLogs.length, testId, forceMeasureVisibleItems]);

  // Track the last dimensions to detect actual size changes vs just visibility changes
  const lastDimensionsRef = useRef<{
    width: number;
    height: number;
  } | null>(null);

  // Handle resize events to recalculate measurements - only on ACTUAL size changes
  useEffect(() => {
    if (!logsContainerRef.current) return;
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!logsContainerRef.current || !isVisible) return;
        const currentWidth = logsContainerRef.current.clientWidth;
        const currentHeight = logsContainerRef.current.clientHeight;

        // Only measure if dimensions actually changed (not just visibility toggle)
        const lastDims = lastDimensionsRef.current;
        if (lastDims && lastDims.width === currentWidth && lastDims.height === currentHeight) {
          return;
        }

        // Width change affects text wrapping - force re-render to re-measure heights
        const widthChanged = !lastDims || lastDims.width !== currentWidth;
        lastDimensionsRef.current = {
          width: currentWidth,
          height: currentHeight
        };
        if (widthChanged) {
          // Capture the first visible line index before re-measuring
          const visibleItems = virtualizerRef.current.getVirtualItems();
          const firstVisibleIndex = visibleItems[0]?.index ?? 0;

          // Increment key to force visible items to re-render and re-measure
          setMeasureKey(k => k + 1);
          virtualizerRef.current.measure();

          // After re-measure, scroll back to the same line index
          requestAnimationFrame(() => {
            virtualizerRef.current.scrollToIndex(firstVisibleIndex, {
              align: "start",
              behavior: "auto"
            });
          });
        } else {
          virtualizerRef.current.measure();
        }
      }, 100);
    };

    // Initialize dimensions
    if (logsContainerRef.current && isVisible) {
      lastDimensionsRef.current = {
        width: logsContainerRef.current.clientWidth,
        height: logsContainerRef.current.clientHeight
      };
    }
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    if (logsContainerRef.current) {
      resizeObserver.observe(logsContainerRef.current);
    }
    const parentElement = logsContainerRef.current?.parentElement;
    const parentResizeObserver = parentElement ? new ResizeObserver(() => {
      handleResize();
    }) : null;
    if (parentElement && parentResizeObserver) {
      parentResizeObserver.observe(parentElement);
    }
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(resizeTimer);
      resizeObserver.disconnect();
      if (parentResizeObserver) {
        parentResizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [isVisible, testId]);

  // Determine which lines should be highlighted
  const highlightedLineNumbers = useMemo(() => {
    if (!highlightRange || highlightRange.start == null || highlightRange.end == null || !stepLogs.length) {
      return new Set<number>();
    }
    const highlighted = new Set<number>();
    for (const log of stepLogs) {
      if (log.log_line_number >= highlightRange.start! && log.log_line_number <= highlightRange.end!) {
        highlighted.add(log.log_line_number);
      }
    }
    return highlighted;
  }, [stepLogs, highlightRange]);

  // Track when we've auto-scrolled for a specific test
  const hasAutoScrolledForTestRef = useRef<string | null>(null);
  const previousTestIdRef = useRef<string | null>(null);

  // Auto-scroll to highlight range ONLY when testId changes (new test selected)
  useLayoutEffect(() => {
    // Reset flag if test.id has changed
    if (previousTestIdRef.current !== testId) {
      hasAutoScrolledForTestRef.current = null;
      previousTestIdRef.current = testId ?? null;
    }

    // Only auto-scroll if:
    // 1. We're visible
    // 2. We have a highlight range
    // 3. We have logs
    // 4. We haven't already scrolled for this test
    const shouldAutoScroll = isVisible && highlightRange && highlightRange.start != null && highlightRange.end != null && stepLogs.length > 0 && hasAutoScrolledForTestRef.current !== testId;
    if (shouldAutoScroll) {
      const startIndex = stepLogs.findIndex(log => log.log_line_number === highlightRange.start);
      if (startIndex >= 0) {
        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          if (logsContainerRef.current && hasAutoScrolledForTestRef.current !== testId) {
            virtualizerRef.current.scrollToIndex(startIndex, {
              align: "start",
              behavior: "auto"
            });
            hasAutoScrolledForTestRef.current = testId ?? null;
          }
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, highlightRange, testId, stepLogs]);

  // Detect if scrolled away from highlighted area (for "return to error" button)
  useEffect(() => {
    if (!isVisible || !logsContainerRef.current || !highlightRange || !highlightRange.start || !highlightRange.end || stepLogs.length === 0) {
      setShowReturnButton(false);
      return;
    }
    const checkScrollPosition = () => {
      if (!logsContainerRef.current || !highlightRange || !highlightRange.start || !highlightRange.end) {
        setShowReturnButton(false);
        return;
      }
      const container = logsContainerRef.current;
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const scrollBottom = scrollTop + containerHeight;
      const startIndex = stepLogs.findIndex(log => log.log_line_number === highlightRange.start);
      const endIndex = stepLogs.findIndex(log => log.log_line_number === highlightRange.end);
      if (startIndex < 0 || endIndex < 0) {
        setShowReturnButton(false);
        return;
      }
      const virtualItems = virtualizerRef.current.getVirtualItems();
      const startVirtualItem = virtualItems.find(item => item.index === startIndex);
      const endVirtualItem = virtualItems.find(item => item.index === endIndex);
      const startIsVisible = startVirtualItem ? startVirtualItem.start >= scrollTop - 50 && startVirtualItem.start + startVirtualItem.size <= scrollBottom + 50 : false;
      const endIsVisible = endVirtualItem ? endVirtualItem.start >= scrollTop - 50 && endVirtualItem.start + endVirtualItem.size <= scrollBottom + 50 : false;
      const bothOffScreen = !startIsVisible && !endIsVisible;
      if (bothOffScreen) {
        let bothAbove = false;
        let bothBelow = false;
        if (startVirtualItem && endVirtualItem) {
          const startBottom = startVirtualItem.start + startVirtualItem.size;
          const endBottom = endVirtualItem.start + endVirtualItem.size;
          const startTop = startVirtualItem.start;
          const endTop = endVirtualItem.start;
          bothAbove = startBottom < scrollTop && endBottom < scrollTop;
          bothBelow = startTop > scrollBottom && endTop > scrollBottom;
        } else {
          const estimatedStartBottom = (startIndex + 1) * ESTIMATED_LOG_LINE_HEIGHT;
          const estimatedEndBottom = (endIndex + 1) * ESTIMATED_LOG_LINE_HEIGHT;
          const estimatedStartTop = startIndex * ESTIMATED_LOG_LINE_HEIGHT;
          const estimatedEndTop = endIndex * ESTIMATED_LOG_LINE_HEIGHT;
          bothAbove = estimatedStartBottom < scrollTop && estimatedEndBottom < scrollTop;
          bothBelow = estimatedStartTop > scrollBottom && estimatedEndTop > scrollBottom;
        }
        const shouldShowButton = bothAbove || bothBelow;
        setShowReturnButton(shouldShowButton);
        if (shouldShowButton) {
          setIsAboveTarget(bothBelow);
        }
      } else {
        setShowReturnButton(false);
      }
    };
    const container = logsContainerRef.current;
    container.addEventListener("scroll", checkScrollPosition, {
      passive: true
    });
    const initialTimer = setTimeout(checkScrollPosition, 100);
    const intervalId = setInterval(checkScrollPosition, 200);
    return () => {
      container.removeEventListener("scroll", checkScrollPosition);
      clearTimeout(initialTimer);
      clearInterval(intervalId);
    };
  }, [isVisible, highlightRange, stepLogs]);
  const handleReturnToTestLogs = () => {
    if (!logsContainerRef.current || !highlightRange?.start) return;
    const targetIndex = stepLogs.findIndex(log => log.log_line_number === highlightRange.start);
    if (targetIndex >= 0) {
      virtualizerRef.current.scrollToIndex(targetIndex, {
        align: "start",
        behavior: "auto"
      });
    }
  };

  // Format timestamp helper
  const formatTimestamp = (timestamp: string): string => {
    try {
      if (typeof timestamp === "string" && timestamp.length >= 19) {
        return timestamp.slice(11, 19); // HH:MM:SS
      }
      return new Date(timestamp).toISOString().slice(11, 19);
    } catch {
      return "--:--:--";
    }
  };
  return <div className="h-full relative">
      {/* Return to test logs button */}
      {showReturnButton && <div className="absolute top-2 right-2 z-20">
          <Button onClick={handleReturnToTestLogs} variant="secondary" size="sm" className="bg-white/70 dark:bg-black/70 backdrop-blur-md border-[var(--color-border)]/50 dark:border-[var(--color-border)]/50 shadow-sm hover:bg-white/80 dark:hover:bg-black/80 cursor-pointer flex items-center gap-1.5">
            {isAboveTarget ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
            Return to test logs
          </Button>
        </div>}
      <div ref={logsContainerRef} className="h-full overflow-y-auto bg-[#FAFAFA] dark:bg-[#111213] font-mono text-xs">
        {isLoading ? <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-neutral-400 dark:border-neutral-500" />
              <div className="text-sm text-neutral-400 dark:text-neutral-500">
                Loading logs...
              </div>
            </div>
          </div> : stepLogs.length === 0 ? <div className="flex items-center justify-center h-full text-sm text-neutral-400">
            No logs available
          </div> : <div key={`virtualized-logs-${testId}`} className="relative" style={{
        height: `${Math.max(virtualizer.getTotalSize(), stepLogs.length * ESTIMATED_LOG_LINE_HEIGHT)}px`,
        width: "100%"
      }}>
            {virtualItems.map(virtualItem => {
          const log = stepLogs[virtualItem.index];
          if (!log) return null;
          const isHighlighted = highlightedLineNumbers.has(log.log_line_number);
          return <div key={`${log.log_idx}-${measureKey}`} data-index={virtualItem.index} ref={virtualizer.measureElement} className="absolute top-0 left-0 w-full" style={{
            transform: `translateY(${virtualItem.start}px)`
          }}>
                  <div className="group flex min-h-[16px] items-stretch">
                    {/* Line number */}
                    <div className={`flex items-start w-16 pr-2 flex-shrink-0 ${isHighlighted ? "bg-blue-100 dark:bg-[var(--brand-yellow-default)]/20" : ""}`}>
                      <div className="w-10 text-neutral-400 dark:text-white text-right text-xs select-none pt-[2px]">
                        {log.log_line_number}
                      </div>
                    </div>

                    {/* Timestamp */}
                    <div className={`flex items-start w-14 pr-2 flex-shrink-0 font-mono text-xs select-none ${isHighlighted ? "bg-blue-100 dark:bg-[var(--brand-yellow-default)]/20" : ""}`}>
                      <div className="w-full text-center text-neutral-400 dark:text-[#737373] pt-[2px]">
                        {formatTimestamp(log.log_timestamp)}
                      </div>
                    </div>

                    {/* Content area */}
                    <div className="relative flex-1 min-w-0">
                      <div className="flex-1 text-xs whitespace-pre-wrap break-words min-w-0 leading-4 border-l border-[var(--color-border)] dark:border-[var(--color-border)] pl-2">
                        <LogLineText text={log.message} searchTerms={[]} />
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </div>;
}